import type { Region } from '~~/core/entities/Region';

import { usePropertyFilters } from '~/composables/propertySearch/context';
import { slugify } from '~~/utils';
import { getRegionCanonicalSlug } from '~~/utils/regionPath';
import { REGION_SERVICE } from '~~/utils/symbols';

const defaultAllowedFilters = [
	'regions',
	'cities',
	'states',
	'locationSlug',
	'transactionType',
	'macroregions',
	'bounds',
	'zoom',
	'commercialIds'
];

export function useRegionInfos(
	filters: Ref<{
		regions?: string[];
		cities?: string[];
		states?: string[];
	}>
) {
	const propertyFiltersApi = usePropertyFilters();
	const route = useRoute();
	const regionService = inject(REGION_SERVICE)!;

	const key = computed(
		() =>
			`region:${filters.value.regions?.[0]}${filters.value.cities?.[0]}${filters.value.states?.[0]}`
	);

	const regionName = computed<string>(() => {
		return (
			regionInfo.value?.hero?.title ||
			regionInfo.value?.name ||
			filters.value.regions?.[0] ||
			''
		);
	});

	const hasInvalidFiltersToShowRegionInfo = () => {
		const filtersFromPath = propertyFiltersApi.getFiltersFromPath(
			route.fullPath
		);

		const allowedFilters = [...defaultAllowedFilters, 'propertyTypes'];

		const hasInvalidFilter = Object.entries(filtersFromPath).some(
			([key, value]) =>
				!allowedFilters.includes(key) &&
				(Array.isArray(value) ? value.length > 0 : Boolean(value))
		);

		return hasInvalidFilter;
	};

	const hasFiltersIncludingNotAllowedFilters = () => {
		const filtersFromPath = propertyFiltersApi.getFiltersFromPath(
			route.fullPath
		);
		const allowedFilters = [...defaultAllowedFilters, 'propertyTypes'];
		const hasNotAllowedFilters = Object.entries(filtersFromPath).some(
			([key, value]) => {
				const isNotAllowed = !allowedFilters.includes(key);
				const hasValue = Array.isArray(value)
					? value.length > 0
					: Boolean(value);

				return isNotAllowed && hasValue;
			}
		);

		return hasNotAllowedFilters;
	};

	const getRegionInfoSlug = () => {
		const region = filters.value.regions?.[0] || '';
		const city = filters.value.cities?.[0] || '';
		const state = filters.value.states?.[0] || '';

		if (region && city && state) {
			return getRegionCanonicalSlug({
				name: region,
				city,
				state
			});
		}

		return slugify(region);
	};

	const { data: regionInfo, refresh: refreshRegionInfo } =
		useAsyncData<Region | null>(
			key.value,
			async () => {
				const slug = getRegionInfoSlug();
				if (
					!slug ||
					hasFiltersIncludingNotAllowedFilters() ||
					hasInvalidFiltersToShowRegionInfo()
				) {
					return null;
				}

				const [region] = await regionService.getOne({ slug });
				return region;
			},
			{
				default: () => null,
				...getCacheAsyncDataConfig<Region | null>(30 * 60 * 1000),
				lazy: false,
				server: true,
				immediate: true,
				watch: [
					() => filters.value.regions,
					() => filters.value.cities,
					() => filters.value.states
				]
			}
		);

	return {
		regionInfo,
		refreshRegionInfo,
		regionName,
		defaultAllowedFilters
	};
}
